import { useEffect, useRef } from 'preact/hooks';
import { FILMS, LATEST, RELEASES, newsDays, noteAction, unseenNews, type Film, type NewsItem } from '../data/whatsNew';
import { calendarFilmOpen, introOpen, loadJSON, panel, saveJSON, storyOpen } from '../state';
import { Sheet } from './chrome';
import { openGallery } from './galleryNav';
import { pointOut } from './Guide';
import { Icon } from './icons';
import { openSizes } from './sizeBoards';

// Once after an update with something new to see: a short card over the sky, which says where to find it again.
// Every update's notes stay in Settings, near the top, under What's new (WhatsNewRow opens the list). A new install
// sees the welcome instead, and starts with every note marked as read.

const lastSeen = () => loadJSON<{ id?: string }>('news', {}).id ?? null;

export function markNewsSeen() {
  saveJSON('news', { id: LATEST });
}

/** Whether there is a note this person has not seen (leaving out a release whose film they have just watched). */
export function newsPending(skip?: string): boolean {
  return unseenNews(lastSeen(), 3, skip).items.length > 0;
}

/**
 * The film to offer first after an update: the newest unseen release's replay, or the newest unseen release that tells
 * its news in a film (even when newer notes have come since, so nobody who missed that update misses its film). `id` is
 * the release the film tells, left out of the card once it is watched; a replay tells none, so its card follows in full.
 */
export function filmPending(): { id?: string; film: 'calendar' } | null {
  const seen = lastSeen();
  for (const r of RELEASES) {
    if (seen && r.id <= seen) break;
    if (r.replay && FILMS[r.replay].offered) return { film: r.replay };
    if (r.film && FILMS[r.film].offered) return { id: r.id, film: r.film };
  }
  return null;
}

/** The newest release told in pages that this person has not seen: its story comes in place of the card. */
export function storyPending(): string | null {
  const seen = lastSeen();
  return RELEASES.find((r) => (!seen || r.id > seen) && r.story?.length)?.id ?? null;
}

/** A film from the list or the card plays as it does from Help: whatever is open closes first. */
function playFilm(f: Film) {
  panel.value = null;
  if (f === 'intro') introOpen.value = true;
  else calendarFilmOpen.value = true;
}

/** Do what a note's button says: play its film, or open the part of Ahillah it is about. */
function runAction(it: NewsItem) {
  const a = noteAction(it);
  if (!a) return;
  if ('film' in a) playFilm(a.film);
  else if (a.show === 'gallery') openGallery();
  else if (a.show === 'sizes') openSizes();
  else {
    const to = a.show;
    panel.value = to;
    if (it.flash) pointOut(it.flash, () => panel.peek() === to);
  }
}

export function WhatsNewCard({ onDone, skip }: { onDone: () => void; skip?: string }) {
  const { items, more } = unseenNews(lastSeen(), 3, skip);
  const ref = useRef<HTMLDivElement>(null);
  // Focus comes to the card, so a screen reader reads it out when it appears.
  useEffect(() => ref.current?.focus({ preventScroll: true }), []);
  const go = (then: () => void) => {
    onDone();
    then();
  };
  return (
    <div class="news-card" role="dialog" aria-labelledby="news-title" tabIndex={-1} ref={ref}>
      <h2 id="news-title">What’s new in Ahillah</h2>
      <div class="news-items">
        {items.map((it) => {
          const a = noteAction(it);
          const body = (
            <>
              <span class="news-icon"><Icon name={it.icon} size={18} /></span>
              <span class="news-text"><b>{it.title}</b><span>{it.text}</span></span>
              {a && <Icon name={'film' in a ? 'play' : 'chevron'} size={15} />}
            </>
          );
          return a ? (
            <button key={it.title} class="news-item" onClick={() => go(() => runAction(it))} aria-label={`${it.title}: ${it.text} ${a.label}`}>{body}</button>
          ) : (
            <div key={it.title} class="news-item">{body}</div>
          );
        })}
      </div>
      <div class="news-actions">
        <button class="btn-ghost" onClick={() => go(() => (panel.value = 'whatsnew'))}>{more > 0 ? `${more} more` : 'See all'}</button>
        <button class="btn" onClick={onDone}>Got it</button>
      </div>
      <p class="news-where"><Icon name="gear" size={14} /> Find this again in Settings, under What’s new</p>
    </div>
  );
}

/** Every update's notes, newest first, a day at a time: panel 'whatsnew', from Settings or the card. */
export function WhatsNewPanel() {
  const days = newsDays();
  return (
    <Sheet onClose={() => (panel.value = null)} size="tall" label="What’s new">
      <h2 class="sheet-title">What’s new</h2>
      {days.map((d, i) => (
        <section key={d.date} class="news-day">
          <h3 class="section-title">
            <span>{d.date}</span>
            {i === 0 && <span class="news-latest">Latest</span>}
          </h3>
          <div class="results">
            {d.stories.map((id) => (
              <button key={id} class="result news-film" onClick={() => { panel.value = null; storyOpen.value = id; }}>
                <span class="result-icon"><Icon name="sparkle" size={16} /></span>
                <span class="result-text">
                  <span class="result-label">Read this update</span>
                  <span class="result-sub">{RELEASES.find((r) => r.id === id)!.story!.length} pages, with photographs.</span>
                </span>
              </button>
            ))}
            {d.films.map((f) => (
              <button key={f} class="result news-film" onClick={() => playFilm(f)}>
                <span class="result-icon"><Icon name="play" size={16} /></span>
                <span class="result-text">
                  <span class="result-label">{FILMS[f].watch}</span>
                  <span class="result-sub">{FILMS[f].name}, in {FILMS[f].length}.</span>
                </span>
              </button>
            ))}
            {d.items.map((it, j) => {
              const a = noteAction(it);
              const title = `news-${i}-${j}`;
              return (
                <div key={j} class="result news-note">
                  <span class="result-icon"><Icon name={it.icon} size={20} /></span>
                  <span class="result-text">
                    <span class="result-label" id={title}>{it.title}</span>
                    <span class="result-sub">{it.text}</span>
                    {a && (
                      <button class="news-go" onClick={() => runAction(it)} aria-describedby={title}>
                        {/* The icon flows with the words, kept beside them by a no-break space when the label wraps. */}
                        {'film' in a ? <><Icon name="play" size={14} />{' '}{a.label}</> : <>{a.label}{' '}<Icon name="chevron" size={14} /></>}
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </Sheet>
  );
}

/** The row in Settings that opens every update's notes, with the date of the latest. */
export function WhatsNewRow() {
  return (
    <button class="list-row btn-row" onClick={() => (panel.value = 'whatsnew')}>
      <span class="row-icon"><Icon name="gift" size={18} /></span>
      <span class="row-text"><span class="row-label">What’s new</span><span class="row-sub">Latest update: {RELEASES[0].date}</span></span>
      <Icon name="chevron" size={16} />
    </button>
  );
}
